import React, { useCallback, useEffect, useState } from "react";
import { Button, Empty, Space, Table, Tag } from "antd";
import { EyeOutlined, ReloadOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";

import { ApiURL } from "../../../../services/apiConstants";
import { post } from "../../../../services/services";

const STATUS_COLORS = {
  pending: "blue",
  overdue: "red",
  completed: "green",
  in_progress: "gold",
  cancelled: "default",
};

const getArrayData = (res) => {
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.response_data)) return res.response_data;
  if (Array.isArray(res?.tasks)) return res.tasks;
  if (Array.isArray(res?.job_tasks)) return res.job_tasks;
  if (Array.isArray(res?.data?.data)) return res.data.data;
  return [];
};

const getWorkOrderId = (record) =>
  record?.work_order_id || record?.id || record?.task_id || record?.job_task_id || record?.job_register_task_id;

const getPrimitiveId = (value) =>
  value && typeof value === "object" ? value.id : value;

const getJobId = (record) =>
  record?.job_id || record?.job?.id || record?.job_register_id || record?.job_register?.id || getPrimitiveId(record?.job);

const formatDate = (date) => {
  if (!date) return "N/A";
  const parsed = dayjs(date);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "N/A";
};

    const isOverdue = (record) => {
        const status = String(record?.status || "").toLowerCase();

        if (status === "completed") return false;

        if (status === "overdue") return true;

        const dueDate = record?.due_date || record?.next_due_date;

        return dueDate
            ? dayjs().isAfter(dayjs(dueDate), "day")
            : false;
    };

const getStatus = (record) =>
  isOverdue(record) ? "overdue" : record?.status || "pending";

const WorkOrderHistoryTab = ({ code, functionCode, machineryData, machineryid, shipId }) => {
  const [loader, setLoader] = useState(false);
  const [workOrders, setWorkOrders] = useState([]);

  const navigate = useNavigate();
  const activeCode = code || functionCode;

  // Fetch Work Orders
  const fetchWorkOrders = useCallback(async () => {
    setLoader(true);
    try {
      const userData = JSON.parse(localStorage.getItem("user_data"));
      const formData = new FormData();

      const companyId = userData?.data?.company?.company_id;
      const activeShipId = shipId || machineryData?.ship_id;
      if (companyId) formData.append("company_id", companyId);
      if (activeShipId) formData.append("ship_id", activeShipId);
      if (activeCode) formData.append("code", activeCode);
      if (machineryid) formData.append("machinery_id", machineryid);
      formData.append("status", "Completed");

      const res = await post(ApiURL.list_work_orders, formData);

      const tasks = res?.status === true ? getArrayData(res) : [];

      setWorkOrders(tasks);
    } catch (error) {
      console.error("Error fetching work orders:", error);
      setWorkOrders([]);
    } finally {
      setLoader(false);
    }
  }, [activeCode, machineryid, shipId, machineryData?.ship_id]);

  useEffect(() => {
    fetchWorkOrders();
  }, [fetchWorkOrders]);

  const openWorkOrder = (record) => {
    const userData = JSON.parse(localStorage.getItem("user_data"));
    const workOrderId = getWorkOrderId(record);
    const companyId =
      record?.company_id || record?.company?.id || userData?.data?.company?.company_id;
    const selectedShipId =
      record?.ship_id || record?.ship?.id || shipId || machineryData?.ship_id;
    const selectedMachineryId =
      record?.machinery_id ||
      record?.machinary_id ||
      record?.job_machinery_id ||
      record?.job?.machinery_id ||
      record?.job?.machinary_id ||
      machineryid ||
      machineryData?.id ||
      machineryData?.machinery_id;
    const selectedJobId = getJobId(record);

    navigate(`/work-order/${workOrderId}`, {
      state: {
        workOrder: record,
        mode: "view",
        workOrderId,
        jobId: selectedJobId,
        companyId,
        shipId: selectedShipId,
        machineryId: selectedMachineryId,
      },
    });
  };

  const sortedWorkOrders = [...workOrders].sort((a, b) => {
    const aOverdue = isOverdue(a);
    const bOverdue = isOverdue(b);

    if (aOverdue !== bOverdue) return aOverdue ? -1 : 1;

    return dayjs(a.due_date || a.next_due_date || "9999-12-31").diff(
      dayjs(b.due_date || b.next_due_date || "9999-12-31")
    );
  });

  const columns = [
    {
      title: "Work Code",
      align: "center",
      render: (_, record) =>
        record.code ||
        record.number ||
        getWorkOrderId(record) ||
        "N/A",
    },
    {
      title: "Title",
      render: (_, record) =>
        record.title ||
        record.job_title ||
        record.job_description ||
        "N/A",
    },
    {
      title: "Component",
      render: (_, record) =>
        record.component ||
        record.machinery_name ||
        record.component_name ||
        machineryData?.machinery_name ||
        "N/A",
    },
    {
      title: "Priority",
      align: "center",
      render: (_, record) => record.priority || "N/A",
    },
    {
      title: "Discipline",
      render: (_, record) =>
        record.responsible ||
        record.designation ||
        record.rank ||
        "N/A",
    },
    {
      title: "Status",
      align: "center",
      render: (_, record) => {
        const status = getStatus(record);

        return (
          <Tag
            color={
              STATUS_COLORS[String(status).toLowerCase()] || "default"
            }
          >
            {String(status).replaceAll("_", " ").toUpperCase()}
          </Tag>
        );
      },
    },
    {
      title: "Original Due Date",
      align: "center",
      render: (_, record) => {
        const dueDate = record.due_date || record.next_due_date;

        return (
          <span style={{ color: isOverdue(record) ? "#D91D3B" : undefined }}>
            {formatDate(dueDate)}
          </span>
        );
      },
    },
    {
      title: "Due Date",
      align: "center",
      render: (_, record) => {
        const dueDate = record.due_date || record.next_due_date;

        return (
          <span style={{ color: isOverdue(record) ? "#D91D3B" : undefined }}>
            {formatDate(dueDate)}
          </span>
        );
      },
    },
    {
      title: "Action",
      align: "center",
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => openWorkOrder(record)}
          >
            View
          </Button>
          <Button size="small" disabled>
            Reschedule
          </Button>
        </Space>

      ),
    },
  ];

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 15,
        }}
      >
        <h4>Work Order History</h4>

        <Button
          icon={<ReloadOutlined />}
          onClick={fetchWorkOrders}
          loading={loader}
        >
          Refresh
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={sortedWorkOrders}
        loading={loader}
        rowKey={(record, index) =>
          getWorkOrderId(record) ||
          `${record.code || "work-order"}-${index}`
        }
        bordered
        pagination={{ pageSize: 10 }}
        className="custom-table"
        locale={{ emptyText: <Empty description="No work orders found" /> }}
      />
    </div>
  );
};

export default WorkOrderHistoryTab;
